// src/lib/thermo/PHFlash.js
// PH flash that *uses your EOSK + Flash utilities*, with robust bracketing & fallbacks.

import { hVap, hLiq } from "./Enthalpy";
import { eosK } from "./EOSK";
import { rachfordRice, phaseCompositions, forceTwoPhaseK } from "./Flash";

function clamp(x, lo, hi) {
  return Math.max(lo, Math.min(hi, x));
}

// Keep these small helpers local so the file is self-contained and ESLint-safe.
function normalize(z) {
  const n = Array.isArray(z) ? z.length : 0;
  if (!n) return [];
  const s = z.reduce((a, v) => a + (Number.isFinite(v) ? v : 0), 0);
  if (!Number.isFinite(s) || s <= 0) return new Array(n).fill(1 / n);
  return z.map((v) => (Number.isFinite(v) ? v / s : 0));
}

// Alias used by the saturated helper. We use eosK (the project's canonical EOS K-value routine)
// and return a consistent object: { K, singlePhase, reason }.
//
// NOTE: Some call sites pass an object (recommended), others used to pass positional args.
function computeEOS_KValues(arg1, P, z, comps, eos, trayIndex, log) {
  let T;
  // Support object-style call: computeEOS_KValues({ T, P, z, comps, eos, trayIndex, log })
  if (arg1 && typeof arg1 === "object" && !Array.isArray(arg1)) {
    ({ T, P, z, comps, eos, trayIndex, log } = arg1);
  } else {
    // Legacy positional call: computeEOS_KValues(T, P, z, comps, eos, trayIndex, log)
    T = arg1;
  }

  const r = eosK(
    P,
    T,
    z,
    comps,
    trayIndex,
    32,
    null,
    null,
    log,
    null,
    "manual",
    eos || "PRSV"
  );

  const K = Array.isArray(r?.K) ? r.K : [];
  return { K, singlePhase: r?.singlePhase ?? null, reason: r?.reason ?? null };
}

function solveRachfordRice(arg) {
  // Return a stable object: { V, x, y, singlePhase?, phase? }
  // Accept object-style call: solveRachfordRice({ z, K, log, trayIndex })
  const z = arg?.z;
  const K = arg?.K;
  const diag = { log: arg?.log, trayIndex: arg?.trayIndex };

  // rrAndCompositions already handles endpoint/single-phase cases.
  const rr = rrAndCompositions(z, K, diag);
  return { V: rr.V, x: rr.x, y: rr.y, singlePhase: rr.singlePhase, phase: rr.phase };
}

// Wilson K-value estimate (robust fallback when EOSK reports single-phase)
// K_i = (Pc_i / P) * exp( 5.37 * (1 + omega_i) * (1 - Tc_i / T) )
function wilsonKValues(T, P, comps) {
  const T_safe = Math.max(1e-6, T);
  const P_safe = Math.max(1e-6, P);
  return comps.map((c) => {
    const Tc = c?.Tc;
    const Pc = c?.Pc;
    const omega = Number.isFinite(c?.omega) ? c.omega : 0.0;

    // If missing properties, fall back to K≈1 (won't crash RR)
    if (!Number.isFinite(Tc) || !Number.isFinite(Pc) || Tc <= 0 || Pc <= 0) return 1.0;

    const exponent = 5.37 * (1 + omega) * (1 - (Tc / T_safe));
    const K = (Pc / P_safe) * Math.exp(exponent);

    // Keep K in a sane numeric range
    return clamp(K, 1e-6, 1e6);
  });
}

// Helper that runs RR + compositions safely
function rrAndCompositions(z, K, diag) {
  let rrVal;
  try {
    rrVal = rachfordRice(z, K, diag);
  } catch {
    rrVal = undefined;
  }

  let V = 0.5;
  if (typeof rrVal === "number" && isFinite(rrVal)) {
    V = clamp(rrVal, 0, 1);
  } else if (rrVal && typeof rrVal === "object") {
    V = clamp(rrVal.beta ?? rrVal.V ?? rrVal.v ?? 0.5, 0, 1);
  }
const rrMeta =
  rrVal && typeof rrVal === "object"
    ? {
        rrStatus: rrVal.status,
        rrReason: rrVal.reason,
        f0: rrVal.f0,
        f1: rrVal.f1,
        f0n: rrVal.f0n,
        f1n: rrVal.f1n,
        iters: rrVal.iters,
        phase: rrVal.phase,
      }
    : {};


  // If RR collapses to an endpoint (all K>1 or all K<1), treat as single-phase.
  // Skipping phaseCompositions avoids numerical issues when V is exactly 0 or 1.
  if (V <= 1e-10) {
    const x = z.slice();
    const sx = x.reduce((a, b) => a + b, 0) || 1;
    const xn = x.map((v) => v / sx);
    return { V: 0, x: xn, y: xn, singlePhase: true, phase: "L", ...rrMeta };
  }
  if (V >= 1 - 1e-10) {
    const y = z.slice();
    const sy = y.reduce((a, b) => a + b, 0) || 1;
    const yn = y.map((v) => v / sy);
    return { V: 1, x: yn, y: yn, singlePhase: true, phase: "V", ...rrMeta };
  }

  let x, y;
  try {
    const res = phaseCompositions(z, K, V);
    if (Array.isArray(res)) [x, y] = res;
    else if (res && Array.isArray(res.x) && Array.isArray(res.y))
      ({ x, y } = res);
    else throw new Error("phaseCompositions returned unexpected shape");
  } catch {
    x = z.slice();
    y = z.slice();
  }

  // normalize
  const sx = x.reduce((a, b) => a + b, 0) || 1;
  const sy = y.reduce((a, b) => a + b, 0) || 1;
  x = x.map((v) => v / sx);
  y = y.map((v) => v / sy);

  return { V, x, y, ...rrMeta };
}

/**
 * PH flash: find T such that H(z,P,T) matches Htarget.
 * Signature kept compatible with the simulator:
 *   flashPH({ Htarget, z, P, Tseed, components, trayIndex, log })
 * Returns { T, V, x, y, status }.
 */
export function flashPH({
  Htarget,
  z,
  P,
  Tseed,
  components,
  trayIndex,
  trays,
  crudeHint,
  eosMode = "auto",
  eosManual = null,
  kij,
  log,
  forceTwoPhase = false,
  // Globally enabled: we do NOT immediately return when the EOS says "singlePhase".
  // Instead, we try a quick Wilson-K RR split, but *only* in that EOS-singlePhase case.
  // If the RR sign test still indicates no 2-phase root, we fall back to single-phase.
  // This keeps normal behavior (e.g., Brent/WCS) unchanged unless the EOS itself
  // trips singlePhase.
  disableSinglePhaseShortCircuit = false,
  murphreeEtaV = null,
}) {
  const comps = components ?? [];
  const Tmin = 180,
    Tmax = 1200;
  const T0 = clamp(Tseed ?? 660, Tmin, Tmax);

  if (log)
    log(
      `[PH ENTER] tray=${trayIndex ?? "?"} Tseed=${T0.toFixed(
        3
      )} Htarget=${Htarget}`
    );

  // Handle absurd / corrupted targets gracefully
  if (!Number.isFinite(Htarget) || Math.abs(Htarget) > 1e6) {
    const ek0 = eosK(
      P,
      T0,
      z,
      comps,
      trayIndex,
      trays,
      crudeHint,
      kij,
      false,
      murphreeEtaV,
      eosMode,
      eosManual
    );
    let rr;
    if (ek0 && ek0.singlePhase) {
      // Diagnostics-only: even if EOSK forces single-phase, still evaluate RR sign-tests
      // on the same K-field (if present) so RRDIAG/RRKTOP can reveal which K's dominate.
      if (ek0.K && Array.isArray(ek0.K) && ek0.K.length === z.length) {
        try {
          rachfordRice(z, ek0.K, { tray: trayIndex, T: T0, P, log });
        } catch {
          // ignore (diagnostic only)
        }
      } else if (log) {
        log(
          `[RRDIAG] tray=${trayIndex ?? "?"}` +
            ` T=${Number.isFinite(T0) ? T0.toFixed(2) : "?"}` +
            ` P=${Number.isFinite(P) ? P.toFixed(0) : "?"}` +
            ` f0=? f1=? sumz=? SzK=? SzOverK=?` +
            ` Kmin=? Kmax=? chosenV=${ek0.phase === "V" ? "1.000" : "0.000"}` +
            ` reason=EOSK_SINGLEPHASE_${ek0.phase ?? "?"}`
        );
      }

      rr = { V: ek0.phase === "V" ? 1 : 0, x: z.slice(), y: z.slice() };
    } else {
      rr = rrAndCompositions(z, ek0.K, { tray: trayIndex, T: T0, P, log, returnObject: true });
    }
    if (log)
      log(
        `[PH OUT] tray=${trayIndex ?? "?"} T=${T0.toFixed(2)} V=${rr.V.toFixed(
          3
        )} (bad-target)`
      );
    return {
      T: T0,
      V: rr.V,
      x: rr.x,
      y: rr.y,
      status: "bad-target",
      dH: NaN,
      Hcalc: NaN,
      Htarget,
    };
  }

  function resid(T) {
    const ek = eosK(
      P,
      T,
      z,
      comps,
      trayIndex,
      trays,
      crudeHint,
      kij,
      false,
      murphreeEtaV,
      eosMode,
      eosManual
    );
    // Only engage any single-phase recovery logic when the EOS itself flags single-phase.
    // This keeps normal two-phase behavior unchanged unless EOS trips.
    const eosSaysSinglePhase = !!(ek && ek.singlePhase);
    let K;
    if (eosSaysSinglePhase) {
      const phase = (ek && typeof ek.phase === "string")
        ? ek.phase
        : ((ek && Number.isFinite(ek.Z) && ek.Z > 0.3) ? "V" : "L");

      // Default behavior: short-circuit single-phase (keeps Brent/WCS behavior unchanged).
      if (!disableSinglePhaseShortCircuit) {
        const V = phase === "V" ? 1 : 0;
        const x = z.slice();
        const y = z.slice();
        const H = (V === 1)
          ? hVap(y, T, comps, P)
          : hLiq(x, T, comps, P);
        return { dH: H - Htarget, V, x, y, singlePhase: true, phase };
      }

      // If requested, do NOT trust EOSK's single-phase classification.
      // Fall back to Wilson K-values so RR can attempt a two-phase split.
      K = wilsonKValues(T, P, comps).map((k) => clamp(k, 1e-8, 1e8));
    } else {
      K = ek.K;
    }

    // By default, respect the EOS phase behavior (V may be 0 or 1).
    // Forcing K to straddle 1.0 can create non-physical V fractions and destabilize column convergence.
    let Kuse = K;
    if (forceTwoPhase) Kuse = forceTwoPhaseK(z, K);

    
// Diagnostics: K range for RR classification
let Kmin = Infinity, Kmax = -Infinity;
for (let i = 0; i < Kuse.length; i++) {
  const k = Kuse[i];
  if (Number.isFinite(k)) {
    if (k < Kmin) Kmin = k;
    if (k > Kmax) Kmax = k;
  }
}
    let rr = rrAndCompositions(z, Kuse, { tray: trayIndex, T, P, log, returnObject: true });
    rr.Kmin = Kmin;
    rr.Kmax = Kmax;
    // Last-resort fallback if something went numerically wrong
    if (!isFinite(rr.V) || !Array.isArray(rr.x) || !Array.isArray(rr.y)) {
      Kuse = forceTwoPhaseK(z, K);
      rr = rrAndCompositions(z, Kuse, { tray: trayIndex, T, P, log, returnObject: true });
    }

    // If RR indicated a single-phase endpoint, we normally short-circuit.
    // We only attempt a "two-phase recovery" when the EOS itself flagged single-phase.
    // That keeps Brent/WCS and normal two-phase cases unchanged.
    if (rr && rr.singlePhase) {
      if (disableSinglePhaseShortCircuit && !forceTwoPhase && eosSaysSinglePhase) {
        const Kforced = forceTwoPhaseK(z, Kuse);
        const rr2 = rrAndCompositions(z, Kforced, { tray: trayIndex, T, P, log, returnObject: true });
        if (rr2 && !rr2.singlePhase && isFinite(rr2.V) && rr2.V > 0 && rr2.V < 1) {
          rr = rr2;
          Kuse = Kforced;
        }
      }
      if (rr && rr.singlePhase) {
        const phase = rr.phase || (rr.V === 1 ? "V" : "L");
        const Vsp = phase === "V" ? 1 : 0;
        const xsp = rr.x || z.slice();
        const ysp = rr.y || z.slice();
        const Hsp = (Vsp === 1) ? hVap(ysp, T, comps, P) : hLiq(xsp, T, comps, P);
        return { dH: Hsp - Htarget, V: Vsp, x: xsp, y: ysp, singlePhase: true, phase };
      }
    }

    const Hv = hVap(rr.y, T, comps, P);
    const Hl = hLiq(rr.x, T, comps, P);
    const H = rr.V * Hv + (1 - rr.V) * Hl;
    return { dH: H - Htarget, ...rr };
  }

  // Initial bracket around seed
  let Tlo = clamp(T0 - 40, Tmin, Tmax);
  let Thi = clamp(T0 + 40, Tmin, Tmax);
  let rlo = resid(Tlo),
    rhi = resid(Thi);
  if (log) {
    log(
      `[PH BRACKET] tray=${trayIndex ?? "?"} ` +
        `Tlo=${Tlo.toFixed(1)} Thi=${Thi.toFixed(1)} ` +
        `dHlo=${rlo.dH.toExponential(2)} dHhi=${rhi.dH.toExponential(2)} ` +
        `signs=[${Math.sign(rlo.dH)},${Math.sign(rhi.dH)}]`
    );
  }

  // Expand bracket if needed
  let expand = 0;
  while (rlo.dH * rhi.dH > 0 && expand < 10) {
    const span = Thi - Tlo || 10;
    Tlo = clamp(Tlo - 0.5 * span, Tmin, Tmax);
    Thi = clamp(Thi + 1.5 * span, Tmin, Tmax);
    rlo = resid(Tlo);
    rhi = resid(Thi);
    expand++;
    if (log) {
      log(
        `[PH BRACKET] tray=${trayIndex ?? "?"} ` +
          `Tlo=${Tlo.toFixed(1)} Thi=${Thi.toFixed(1)} ` +
          `dHlo=${rlo.dH.toExponential(2)} dHhi=${rhi.dH.toExponential(2)} ` +
          `signs=[${Math.sign(rlo.dH)},${Math.sign(rhi.dH)}]`
      );
    }
  }

  // If still no sign change, do a safe Newton-ish step and pick the best
  if (rlo.dH * rhi.dH > 0) {
    const T1 = clamp(T0 + 5, Tmin, Tmax);
    const r0 = resid(T0),
      r1 = resid(T1);
    const dHdT = (r1.dH - r0.dH) / (T1 - T0) || 1e-6;
    const Tn = clamp(T0 - r0.dH / dHdT, Tmin, Tmax);
    const rn = resid(Tn);
    const candidates = [
  // Penalize returning exactly the seed (T0) to avoid "temperature attractor" collapse.
  [T0, r0, 1.0],
  [T1, r1, 0.5],
  [Tn, rn, 0.5],
];
const best = candidates
  .map(([T, r, penalty]) => [T, r, Math.abs(r.dH) * penalty])
  .sort((a, b) => a[2] - b[2])[0];
const [Tb, rb] = best;
    if (log)
      log(
        `[PH OUT] tray=${trayIndex ?? "?"} T=${Tb.toFixed(2)} V=${rb.V.toFixed(
          3
        )} (no-bracket)`
      );
    return {
      T: Tb,
      V: rb.V,
      x: rb.x,
      y: rb.y,
      status: "no-bracket-soft",
      dH: rb && typeof rb.dH === "number" ? rb.dH : NaN,
      Hcalc:
        rb && typeof rb.dH === "number" && Number.isFinite(Htarget)
          ? Htarget + rb.dH
          : NaN,
      Htarget,
      singlePhase: !!(rb && rb.singlePhase),
      phase: rb && rb.phase,
    };
  }

  // Bisection on the bracket
  let Tmid = T0,
    rmid = rlo;
  for (let it = 0; it < 50; it++) {
    Tmid = 0.5 * (Tlo + Thi);
    rmid = resid(Tmid);
// Nudge off "enthalpy shelf" cases where dH is tiny but V is pinned at 0/1,
// which can otherwise cause flat temperature profiles in liquid-dominated trays.
if (Math.abs(rmid.dH) < 1e-6 && (rmid.V < 1e-4 || rmid.V > 1 - 1e-4)) {
  const Ttry = clamp(Tmid + 2.0, Tmin, Tmax);
  if (Ttry !== Tmid) {
    Tmid = Ttry;
    rmid = resid(Tmid);
  }
}

    if (Math.abs(rmid.dH) < 1e-6 || Math.abs(Thi - Tlo) < 1e-6) break;
    if (rlo.dH * rmid.dH <= 0) {
      Thi = Tmid;
      rhi = rmid;
    } else {
      Tlo = Tmid;
      rlo = rmid;
    }
  }

  if (log)
    log(
      `[PH OUT] tray=${trayIndex ?? "?"} T=${Tmid.toFixed(2)} V=${rmid.V.toFixed(3)}${rmid.singlePhase ? " (singlePhase:" + (rmid.phase || (rmid.V===1?"V":"L")) + ")" : ""}`
    );
  return {
    T: Tmid,
    V: rmid.V,
    x: rmid.x,
    y: rmid.y,
    status: "ok",
    dH: rmid.dH,
    Hcalc: Number.isFinite(Htarget) && typeof rmid.dH === "number" ? Htarget + rmid.dH : NaN,
    Htarget,
    singlePhase: !!rmid.singlePhase,
    phase: rmid.phase,
  };
}

export default flashPH;

// ------------------------------------------------------------
// Saturated PH flash helper (used for reboiler/condensers)
//
// Rationale:
// - Industrial reboilers/condensers typically deliver *saturated* vapor/liquid
//   (no arbitrary superheat/subcooling in the simple column model).
// - This wrapper constrains the PH solve to the saturation envelope
//   [T_bubble, T_dew] based on K-values, and clamps enthalpy targets outside
//   that envelope to avoid runaway temperatures.
// ------------------------------------------------------------

function solveBubblePointT({ z, P, eos, trayIndex, components, Tseed, Tmin = 250, Tmax = 1400, maxIter = 80, log }) {
  // Bubble point condition (K-based): S(T) = sum(z_i * K_i(T)) = 1
  const S = (T) => {
    const { K } = computeEOS_KValues({ z, P, T, comps: components, eos, trayIndex, log });
    let s = 0;
    for (let i = 0; i < z.length; i++) s += z[i] * K[i];
    return s;
  };

  // Try to bracket a root for S(T)-1.
  let Tlo = clamp(Tseed ?? 0.5 * (Tmin + Tmax), Tmin, Tmax);
  let Thi = Tlo;
  let flo = S(Tlo) - 1;
  let fhi = flo;

  // Expand outwards multiplicatively until we bracket.
  for (let k = 0; k < 30 && Math.sign(flo) === Math.sign(fhi); k++) {
    const span = Math.max(5, 0.15 * (k + 1) * (Tmax - Tmin) / 30);
    Tlo = clamp(Tlo - span, Tmin, Tmax);
    Thi = clamp(Thi + span, Tmin, Tmax);
    flo = S(Tlo) - 1;
    fhi = S(Thi) - 1;
    if (Tlo === Tmin && Thi === Tmax) break;
  }

  // If still not bracketed, fall back to a coarse scan.
  if (Math.sign(flo) === Math.sign(fhi)) {
    let bestT = clamp(Tseed ?? 0.5 * (Tmin + Tmax), Tmin, Tmax);
    let bestAbs = Number.POSITIVE_INFINITY;
    const n = 40;
    for (let j = 0; j <= n; j++) {
      const T = Tmin + (j / n) * (Tmax - Tmin);
      const f = S(T) - 1;
      const a = Math.abs(f);
      if (a < bestAbs) {
        bestAbs = a;
        bestT = T;
      }
    }
    return bestT;
  }

  // Bisection.
  let a = Tlo,
    b = Thi,
    fa = flo,
    fb = fhi;
  for (let it = 0; it < maxIter; it++) {
    const m = 0.5 * (a + b);
    const fm = S(m) - 1;
    if (Math.abs(fm) < 1e-6 || Math.abs(b - a) < 1e-4) return m;
    if (Math.sign(fm) === Math.sign(fa)) {
      a = m;
      fa = fm;
    } else {
      b = m;
      fb = fm;
    }
  }
  return 0.5 * (a + b);
}

function solveDewPointT({ z, P, eos, trayIndex, components, Tseed, Tmin = 250, Tmax = 1400, maxIter = 80, log }) {
  // Dew point condition (K-based): D(T) = sum(z_i / K_i(T)) = 1
  const D = (T) => {
    const { K } = computeEOS_KValues({ z, P, T, comps: components, eos, trayIndex, log });
    let s = 0;
    for (let i = 0; i < z.length; i++) s += z[i] / Math.max(1e-30, K[i]);
    return s;
  };

  let Tlo = clamp(Tseed ?? 0.5 * (Tmin + Tmax), Tmin, Tmax);
  let Thi = Tlo;
  let flo = D(Tlo) - 1;
  let fhi = flo;

  for (let k = 0; k < 30 && Math.sign(flo) === Math.sign(fhi); k++) {
    const span = Math.max(5, 0.15 * (k + 1) * (Tmax - Tmin) / 30);
    Tlo = clamp(Tlo - span, Tmin, Tmax);
    Thi = clamp(Thi + span, Tmin, Tmax);
    flo = D(Tlo) - 1;
    fhi = D(Thi) - 1;
    if (Tlo === Tmin && Thi === Tmax) break;
  }

  if (Math.sign(flo) === Math.sign(fhi)) {
    let bestT = clamp(Tseed ?? 0.5 * (Tmin + Tmax), Tmin, Tmax);
    let bestAbs = Number.POSITIVE_INFINITY;
    const n = 40;
    for (let j = 0; j <= n; j++) {
      const T = Tmin + (j / n) * (Tmax - Tmin);
      const f = D(T) - 1;
      const a = Math.abs(f);
      if (a < bestAbs) {
        bestAbs = a;
        bestT = T;
      }
    }
    return bestT;
  }

  let a = Tlo,
    b = Thi,
    fa = flo,
    fb = fhi;
  for (let it = 0; it < maxIter; it++) {
    const m = 0.5 * (a + b);
    const fm = D(m) - 1;
    if (Math.abs(fm) < 1e-6 || Math.abs(b - a) < 1e-4) return m;
    if (Math.sign(fm) === Math.sign(fa)) {
      a = m;
      fa = fm;
    } else {
      b = m;
      fb = fm;
    }
  }
  return 0.5 * (a + b);
}

export function flashPH_saturated({
  z,
  P,
  Htarget,
  components,
  eos,
  trayIndex,
  Tseed,
  Tmax = 1400,
  Tmin = 250,
  maxIter = 80,
  log,
}) {
  // Determine saturation envelope.
  const Tbub = solveBubblePointT({ z, P, eos, trayIndex, components, Tseed, Tmin, Tmax, log });
  const Tdew = solveDewPointT({ z, P, eos, trayIndex, components, Tseed: Tbub, Tmin, Tmax, log });

  // Bubble: V≈0, Dew: V≈1
  const Hbub = hLiq(z, Tbub, components, P);
  const Hdew = hVap(z, Tdew, components, P);

  if (!Number.isFinite(Htarget)) {
    return {
      T: Tbub,
      V: 0,
      x: z.slice(),
      y: z.slice(),
      singlePhase: "L",
      reason: "bad-target",
      Tbub,
      Tdew,
      Hbub,
      Hdew,
    };
  }

  // Clamp outside saturation envelope (avoid superheat/subcooling in simple model).
  if (Htarget <= Hbub) {
    const { K } = computeEOS_KValues({ z, P, T: Tbub, comps: components, eos, trayIndex, log });
    const y = normalize(z.map((zi, i) => zi * K[i]));
    return { T: Tbub, V: 0, x: z.slice(), y, singlePhase: "L", reason: "sat-clamp-L", Tbub, Tdew, Hbub, Hdew };
  }
  if (Htarget >= Hdew) {
    const { K } = computeEOS_KValues({ z, P, T: Tdew, comps: components, eos, trayIndex, log });
    const x = normalize(z.map((zi, i) => zi / Math.max(1e-30, K[i])));
    return { T: Tdew, V: 1, x, y: z.slice(), singlePhase: "V", reason: "sat-clamp-V", Tbub, Tdew, Hbub, Hdew };
  }

  // Two-phase solve constrained to [Tbub, Tdew].
  const Hresid = (T) => {
    let { K, singlePhase, reason } = computeEOS_KValues({ z, P, T, comps: components, eos, trayIndex, log });
    // Force a two-phase K set to guarantee RR has a root (avoid singlePhase shortcuts).
    if (singlePhase) {
      K = forceTwoPhaseK({ z, K });
      singlePhase = null;
      reason = "FORCED_TWOPHASE";
    }
    const rr = solveRachfordRice({ z, K, log, trayIndex });
    const V = rr.V;
    const x = rr.x;
    const y = rr.y;
    const H = V * hVap(y, T, components, P) + (1 - V) * hLiq(x, T, components, P);
    return { dH: H - Htarget, V, x, y, K, reason };
  };

  let a = Tbub,
    b = Tdew;
  let ra = Hresid(a);
  let rb = Hresid(b);
  // By construction Htarget is within [Hbub,Hdew], so we should bracket.
  // But numerical issues can still happen; fall back to mid if needed.
  if (!Number.isFinite(ra.dH) || !Number.isFinite(rb.dH) || Math.sign(ra.dH) === Math.sign(rb.dH)) {
    const m = 0.5 * (a + b);
    const rm = Hresid(m);
    return { T: m, V: rm.V, x: rm.x, y: rm.y, singlePhase: null, reason: "sat-fallback", Tbub, Tdew, Hbub, Hdew };
  }

  for (let it = 0; it < maxIter; it++) {
    const m = 0.5 * (a + b);
    const rm = Hresid(m);
    if (Math.abs(rm.dH) < 1e-6 || Math.abs(b - a) < 1e-4) {
      return { T: m, V: rm.V, x: rm.x, y: rm.y, singlePhase: null, reason: rm.reason, Tbub, Tdew, Hbub, Hdew };
    }
    if (Math.sign(rm.dH) === Math.sign(ra.dH)) {
      a = m;
      ra = rm;
    } else {
      b = m;
      rb = rm;
    }
  }
  const m = 0.5 * (a + b);
  const rm = Hresid(m);
  return { T: m, V: rm.V, x: rm.x, y: rm.y, singlePhase: null, reason: "sat-maxiter", Tbub, Tdew, Hbub, Hdew };
}
